import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Search, Users, User, Save, X, Trash2, Pencil, Database, Shield, FileSpreadsheet, FileText } from "lucide-react";
import { toast } from "sonner";
import { downloadMastersCsv, downloadMastersPdf } from "@/lib/mastersExport";

export default function Masters() {
  const { user } = useAuth();
  const [tab, setTab] = useState("customers");

  if (user && user.role !== "admin") {
    return (
      <div className="brut-card p-10 text-center max-w-md mx-auto" data-testid="masters-denied">
        <Shield className="mx-auto mb-3 text-[#E63946]" size={36} strokeWidth={2.5} />
        <div className="font-heading font-black text-2xl">ADMIN ONLY</div>
        <div className="text-sm text-[#666] mt-2 uppercase tracking-wider">Masters management is restricted to admin accounts.</div>
      </div>
    );
  }

  return (
    <div data-testid="masters-page">
      <div className="flex items-center gap-3 mb-6">
        <Database className="text-[#E63946]" strokeWidth={3} />
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#E63946] font-bold mb-1">── DIRECTORY</div>
          <h1 className="font-heading font-black text-4xl tracking-tighter">MASTERS.</h1>
        </div>
      </div>

      <div className="flex gap-3 mb-5">
        <button onClick={() => setTab("customers")} data-testid="tab-customers"
                className={`brut-btn ${tab === "customers" ? "brut-btn-dark" : "brut-btn-secondary"}`}>
          <User size={16} strokeWidth={3} /> CUSTOMERS
        </button>
        <button onClick={() => setTab("agents")} data-testid="tab-agents"
                className={`brut-btn ${tab === "agents" ? "brut-btn-dark" : "brut-btn-secondary"}`}>
          <Users size={16} strokeWidth={3} /> AGENTS
        </button>
      </div>

      {tab === "customers" ? <CustomersPanel /> : <AgentsPanel />}
    </div>
  );
}

/* ============== CUSTOMERS ============== */
function CustomersPanel() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/customers", { params: q ? { q } : {} });
      setRows(data || []);
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [q]); // eslint-disable-line

  const startEdit = (r) => { setEditingId(r.id); setDraft({ ...r }); };
  const cancelEdit = () => { setEditingId(null); setDraft({}); };

  const save = async () => {
    try {
      await api.patch(`/customers/${editingId}`, {
        name: draft.name, city: draft.city, phone: draft.phone, agent: draft.agent,
      });
      toast.success("Customer updated");
      cancelEdit(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete customer ${r.name}?`)) return;
    try {
      await api.delete(`/customers/${r.id}`);
      toast.success("Deleted"); load();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <div className="space-y-4" data-testid="customers-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="relative max-w-sm flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" />
          <input className="brut-input pl-9" data-testid="search-customers"
                 placeholder="Search name, phone, city…"
                 value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="flex gap-2">
          <button onClick={() => downloadMastersCsv("customers", rows)} className="brut-btn brut-btn-secondary !py-2 text-xs" data-testid="export-customers-csv">
            <FileSpreadsheet size={14} strokeWidth={3} /> CSV
          </button>
          <button onClick={() => downloadMastersPdf("customers", rows).then(() => toast.success("PDF downloaded"))}
                  className="brut-btn brut-btn-primary !py-2 text-xs" data-testid="export-customers-pdf">
            <FileText size={14} strokeWidth={3} /> PDF
          </button>
        </div>
      </div>

      <div className="brut-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] text-white">
            <tr className="text-left">
              <th className="px-4 py-3 text-xs uppercase">Name</th>
              <th className="px-4 py-3 text-xs uppercase">City</th>
              <th className="px-4 py-3 text-xs uppercase">Phone</th>
              <th className="px-4 py-3 text-xs uppercase">Agent</th>
              <th className="px-4 py-3 text-xs uppercase w-32">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="5" className="px-4 py-8 text-center text-sm text-[#666]">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan="5" className="px-4 py-8 text-center text-sm text-[#666] uppercase tracking-wider">No customers</td></tr>}
            {!loading && rows.map((r) => (
              <tr key={r.id} className="border-b border-black/15" data-testid={`cust-row-${r.id}`}>
                {editingId === r.id ? (
                  <>
                    <td className="px-2 py-2"><input className="brut-input !py-2 !px-2 text-sm" data-testid="edit-cust-name" value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></td>
                    <td className="px-2 py-2"><input className="brut-input !py-2 !px-2 text-sm" data-testid="edit-cust-city" value={draft.city || ""} onChange={(e) => setDraft({ ...draft, city: e.target.value })} /></td>
                    <td className="px-2 py-2"><input className="brut-input !py-2 !px-2 text-sm" data-testid="edit-cust-phone" value={draft.phone || ""} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} /></td>
                    <td className="px-2 py-2"><input className="brut-input !py-2 !px-2 text-sm" data-testid="edit-cust-agent" value={draft.agent || ""} onChange={(e) => setDraft({ ...draft, agent: e.target.value })} /></td>
                    <td className="px-2 py-2">
                      <div className="flex gap-1">
                        <button onClick={save} className="brut-btn brut-btn-primary !py-1 !px-2 text-xs" data-testid="save-cust"><Save size={12} strokeWidth={3} /></button>
                        <button onClick={cancelEdit} className="brut-btn brut-btn-secondary !py-1 !px-2 text-xs" data-testid="cancel-cust"><X size={12} strokeWidth={3} /></button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-3 font-bold">{r.name}</td>
                    <td className="px-4 py-3">{r.city || "—"}</td>
                    <td className="px-4 py-3 font-mono">{r.phone || "—"}</td>
                    <td className="px-4 py-3">{r.agent || "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button onClick={() => startEdit(r)} className="brut-btn brut-btn-secondary !py-1 !px-2 text-xs" data-testid={`edit-cust-${r.id}`}><Pencil size={12} strokeWidth={3} /></button>
                        <button onClick={() => del(r)} className="brut-btn brut-btn-secondary !py-1 !px-2 text-xs text-[#E63946]" data-testid={`del-cust-${r.id}`}><Trash2 size={12} strokeWidth={3} /></button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ============== AGENTS ============== */
function AgentsPanel() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({});
  const [newAgent, setNewAgent] = useState({ name: "", phone: "" });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/agents", { params: q ? { q } : {} });
      setRows(data || []);
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [q]); // eslint-disable-line

  const startEdit = (r) => { setEditingId(r.id); setDraft({ ...r }); };
  const cancelEdit = () => { setEditingId(null); setDraft({}); };

  const save = async () => {
    try {
      await api.patch(`/agents/${editingId}`, { name: draft.name, phone: draft.phone });
      toast.success("Agent updated"); cancelEdit(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete agent ${r.name}?`)) return;
    try { await api.delete(`/agents/${r.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error("Delete failed"); }
  };

  const add = async () => {
    if (!newAgent.name.trim()) { toast.error("Name required"); return; }
    try {
      await api.post("/agents", { name: newAgent.name.trim(), phone: newAgent.phone.trim() });
      toast.success("Agent saved"); setNewAgent({ name: "", phone: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  return (
    <div className="space-y-4" data-testid="agents-panel">
      <div className="brut-card p-4">
        <div className="label-mono">Add a new agent</div>
        <div className="grid sm:grid-cols-[1fr_180px_auto] gap-2">
          <input className="brut-input" placeholder="Agent name" value={newAgent.name}
                 data-testid="new-agent-name"
                 onChange={(e) => setNewAgent({ ...newAgent, name: e.target.value })} />
          <input className="brut-input" placeholder="Phone (10-digit)" inputMode="numeric" value={newAgent.phone}
                 data-testid="new-agent-phone"
                 onChange={(e) => setNewAgent({ ...newAgent, phone: e.target.value })} />
          <button onClick={add} className="brut-btn brut-btn-primary" data-testid="add-agent-btn">SAVE</button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="relative max-w-sm flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" />
          <input className="brut-input pl-9" data-testid="search-agents"
                 placeholder="Search agents…"
                 value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <div className="flex gap-2">
          <button onClick={() => downloadMastersCsv("agents", rows)} className="brut-btn brut-btn-secondary !py-2 text-xs" data-testid="export-agents-csv">
            <FileSpreadsheet size={14} strokeWidth={3} /> CSV
          </button>
          <button onClick={() => downloadMastersPdf("agents", rows).then(() => toast.success("PDF downloaded"))}
                  className="brut-btn brut-btn-primary !py-2 text-xs" data-testid="export-agents-pdf">
            <FileText size={14} strokeWidth={3} /> PDF
          </button>
        </div>
      </div>

      <div className="brut-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0A0A] text-white">
            <tr className="text-left">
              <th className="px-4 py-3 text-xs uppercase">Name</th>
              <th className="px-4 py-3 text-xs uppercase">Phone</th>
              <th className="px-4 py-3 text-xs uppercase">Updated</th>
              <th className="px-4 py-3 text-xs uppercase w-32">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan="4" className="px-4 py-8 text-center text-sm text-[#666]">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan="4" className="px-4 py-8 text-center text-sm text-[#666] uppercase tracking-wider">No agents</td></tr>}
            {!loading && rows.map((r) => (
              <tr key={r.id} className="border-b border-black/15" data-testid={`agent-row-${r.id}`}>
                {editingId === r.id ? (
                  <>
                    <td className="px-2 py-2"><input className="brut-input !py-2 !px-2 text-sm" data-testid="edit-agent-name" value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></td>
                    <td className="px-2 py-2"><input className="brut-input !py-2 !px-2 text-sm" data-testid="edit-agent-phone" value={draft.phone || ""} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} /></td>
                    <td className="px-4 py-3 text-xs text-[#666]">—</td>
                    <td className="px-2 py-2">
                      <div className="flex gap-1">
                        <button onClick={save} className="brut-btn brut-btn-primary !py-1 !px-2 text-xs" data-testid="save-agent"><Save size={12} strokeWidth={3} /></button>
                        <button onClick={cancelEdit} className="brut-btn brut-btn-secondary !py-1 !px-2 text-xs" data-testid="cancel-agent"><X size={12} strokeWidth={3} /></button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-3 font-bold">{r.name}</td>
                    <td className="px-4 py-3 font-mono">{r.phone || "—"}</td>
                    <td className="px-4 py-3 text-xs text-[#666]">{r.updated_at ? new Date(r.updated_at).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button onClick={() => startEdit(r)} className="brut-btn brut-btn-secondary !py-1 !px-2 text-xs" data-testid={`edit-agent-${r.id}`}><Pencil size={12} strokeWidth={3} /></button>
                        <button onClick={() => del(r)} className="brut-btn brut-btn-secondary !py-1 !px-2 text-xs text-[#E63946]" data-testid={`del-agent-${r.id}`}><Trash2 size={12} strokeWidth={3} /></button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
